import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import VodService from '../../../services/VodService';
import {
  PageHeader, Card, Button, Badge, EmptyState, Field, Input, Toggle,
} from '../../ui';

const fmtDuration = (sec) => {
  if (!sec && sec !== 0) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};
const fmtSize = (bytes) => {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
};

const emptyDirForm = { name: '', description: '', path: '' };

const MediaDirectoryManager = () => {
  const [directories, setDirectories] = useState([]);
  const [activeDirId, setActiveDirId] = useState('all');
  const [assets, setAssets] = useState([]);
  const [loadingDirs, setLoadingDirs] = useState(true);
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Directory create/edit
  const [showDirModal, setShowDirModal] = useState(false);
  const [editingDir, setEditingDir] = useState(null); // null = create
  const [dirForm, setDirForm] = useState(emptyDirForm);
  const [savingDir, setSavingDir] = useState(false);

  // Video preview / edit
  const [previewAsset, setPreviewAsset] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetForm, setAssetForm] = useState({ title: '', description: '' });
  const [savingAsset, setSavingAsset] = useState(false);

  // Failover toggle
  const [failoverEnabled, setFailoverEnabled] = useState(false);
  const [failoverLoading, setFailoverLoading] = useState(false);

  const fetchDirectories = useCallback(async () => {
    try {
      setLoadingDirs(true);
      const res = await VodService.getAllDirectories();
      if (res.success) setDirectories(res.directories || []);
    } catch (err) {
      toast.error('Failed to load directories');
    } finally {
      setLoadingDirs(false);
    }
  }, []);

  const fetchAssets = useCallback(async (dirId) => {
    try {
      setLoadingAssets(true);
      const res = dirId === 'all'
        ? await VodService.getAllAssets()
        : await VodService.getAssetsByDirectory(dirId);
      if (res.success) setAssets(res.assets || []);
    } catch (err) {
      toast.error('Failed to load videos');
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  const fetchFailoverSettings = useCallback(async () => {
    try {
      const res = await VodService.getFailoverSettings();
      if (res.success) setFailoverEnabled(!!res.enabled);
    } catch (_) { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchDirectories(); fetchFailoverSettings(); }, [fetchDirectories, fetchFailoverSettings]);
  useEffect(() => { fetchAssets(activeDirId); }, [activeDirId, fetchAssets]);

  // Keep in sync with the topbar Failover toggle.
  useEffect(() => {
    const sync = (e) => { if (typeof e?.detail?.on === 'boolean') setFailoverEnabled(e.detail.on); };
    window.addEventListener('teve:failover-changed', sync);
    return () => window.removeEventListener('teve:failover-changed', sync);
  }, []);

  const totalCount = directories.reduce((sum, d) => sum + (d.asset_count || 0), 0);

  const toggleFailover = async () => {
    const next = !failoverEnabled;
    setFailoverLoading(true);
    setFailoverEnabled(next); // optimistic
    try {
      const res = await VodService.setFailoverSettings(next);
      if (res.success) {
        window.dispatchEvent(new CustomEvent('teve:failover-changed', { detail: { on: next } }));
        toast.success(next
          ? 'Auto-failover is ON — live drops will shuffle-play your videos after a 10s grace period.'
          : 'Auto-failover is OFF.');
      }
    } catch (err) {
      setFailoverEnabled(!next); // revert
      toast.error('Failed to update auto-failover setting');
    } finally {
      setFailoverLoading(false);
    }
  };

  // ---- Directory CRUD ----
  const openCreateDir = () => { setEditingDir(null); setDirForm(emptyDirForm); setShowDirModal(true); };
  const openEditDir = (dir) => {
    setEditingDir(dir);
    setDirForm({ name: dir.name, description: dir.description || '', path: dir.path || '' });
    setShowDirModal(true);
  };
  const saveDirectory = async (e) => {
    e.preventDefault();
    setSavingDir(true);
    try {
      const res = editingDir
        ? await VodService.updateDirectory(editingDir.id, dirForm)
        : await VodService.createDirectory(dirForm);
      if (res.success) {
        toast.success(editingDir ? 'Directory updated' : 'Directory created');
        setShowDirModal(false);
        fetchDirectories();
      }
    } catch {
      toast.error(editingDir ? 'Failed to update directory' : 'Failed to create directory');
    } finally {
      setSavingDir(false);
    }
  };
  const deleteDirectory = (dir) => {
    swal({
      title: `Delete "${dir.name}"?`,
      text: 'All videos inside this directory will also be deleted. This cannot be undone.',
      icon: 'warning', buttons: ['Cancel', 'Delete'], dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      try {
        const res = await VodService.deleteDirectory(dir.id);
        if (res.success) {
          toast.success('Directory deleted');
          if (activeDirId === dir.id) setActiveDirId('all');
          fetchDirectories();
        }
      } catch { toast.error('Failed to delete directory'); }
    });
  };

  // ---- Video (asset) actions ----
  const openEditAsset = (asset) => {
    setEditingAsset(asset);
    setAssetForm({ title: asset.title || '', description: asset.description || '' });
  };
  const deleteAsset = (asset) => {
    swal({
      title: `Delete "${asset.title}"?`,
      text: 'This video will be permanently removed from storage.',
      icon: 'warning', buttons: ['Cancel', 'Delete'], dangerMode: true,
    }).then(async (ok) => {
      if (!ok) return;
      try {
        const res = await VodService.deleteAsset(asset.id);
        if (res.success) {
          toast.success('Video deleted');
          setAssets((prev) => prev.filter((a) => a.id !== asset.id));
          fetchDirectories();
        }
      } catch { toast.error('Failed to delete video'); }
    });
  };

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />

      <PageHeader
        title="Media Library"
        subtitle="Organize your VOD videos into directories, preview them, and control auto-failover"
        actions={
          <>
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-border tw-border-line tw-bg-surface tw-px-3.5 tw-py-2">
              <Toggle checked={failoverEnabled} onChange={toggleFailover} />
              <div className="tw-leading-tight">
                <div className="tw-text-sm tw-font-semibold tw-text-heading">
                  Auto-Failover <Badge tone={failoverEnabled ? 'good' : 'neutral'} className="tw-ml-1">{failoverLoading ? '…' : failoverEnabled ? 'ON' : 'OFF'}</Badge>
                </div>
                <div className="tw-text-xs tw-text-content-faint">Shuffle all videos if live drops for 10s</div>
              </div>
            </div>
            <Button icon="fa-folder-plus" onClick={openCreateDir}>New Directory</Button>
          </>
        }
      />

      {/* Directory rail */}
      <div className="tw-mb-6 tw-flex tw-flex-wrap tw-gap-2">
        <button
          onClick={() => setActiveDirId('all')}
          className={`tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-transition ${
            activeDirId === 'all' ? 'tw-border-brand tw-bg-brand-soft tw-text-brand' : 'tw-border-line tw-bg-surface tw-text-content-muted hover:tw-text-heading'
          }`}
        >
          <i className="fa-solid fa-layer-group" /> All Videos
          <Badge tone="neutral">{totalCount}</Badge>
        </button>

        {directories.map((dir) => (
          <div
            key={dir.id}
            className={`tw-group tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-transition ${
              activeDirId === dir.id ? 'tw-border-brand tw-bg-brand-soft tw-text-brand' : 'tw-border-line tw-bg-surface tw-text-content-muted hover:tw-text-heading'
            }`}
          >
            <button className="tw-flex tw-items-center tw-gap-2" onClick={() => setActiveDirId(dir.id)}>
              <i className="fa-solid fa-folder" /> {dir.name}
              <Badge tone="neutral">{dir.asset_count || 0}</Badge>
            </button>
            <button className="tw-text-content-faint hover:tw-text-heading" title="Edit directory" onClick={() => openEditDir(dir)}>
              <i className="fa-solid fa-pen tw-text-xs" />
            </button>
            <button className="tw-text-content-faint hover:tw-text-live" title="Delete directory" onClick={() => deleteDirectory(dir)}>
              <i className="fa-solid fa-trash tw-text-xs" />
            </button>
          </div>
        ))}
      </div>

      {loadingDirs && directories.length === 0 ? (
        <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>
      ) : directories.length === 0 ? (
        <Card>
          <EmptyState
            icon="fa-folder-open"
            title="No directories yet"
            hint="Create a directory to start organizing your VOD videos."
            action={<Button icon="fa-folder-plus" onClick={openCreateDir}>Create Directory</Button>}
          />
        </Card>
      ) : (
        <Card
          title={activeDirId === 'all' ? 'All Videos' : directories.find((d) => d.id === activeDirId)?.name}
          subtitle={`${assets.length} video${assets.length === 1 ? '' : 's'}`}
        >
          {loadingAssets ? (
            <div className="tw-py-14 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-xl tw-text-brand" /></div>
          ) : assets.length === 0 ? (
            <EmptyState icon="fa-film" title="No videos here yet" hint="Upload videos from the VOD Uploader page to see them in this directory." />
          ) : (
            <div className="tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-3 lg:tw-grid-cols-4 xl:tw-grid-cols-5">
              {assets.map((asset) => (
                <div key={asset.id} className="tw-group tw-overflow-hidden tw-rounded-xl tw-border tw-border-line tw-bg-surface tw-transition hover:tw-shadow-card">
                  <button
                    className="tw-relative tw-block tw-h-32 tw-w-full tw-cursor-pointer tw-overflow-hidden tw-bg-ink-950"
                    onClick={() => setPreviewAsset(asset)}
                    title="Preview"
                  >
                    <img
                      src={VodService.getAssetThumbnailUrl(asset.id)}
                      alt=""
                      className="tw-h-full tw-w-full tw-object-cover tw-opacity-90 tw-transition group-hover:tw-opacity-60"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                      <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-black/55 tw-text-white tw-opacity-0 tw-transition group-hover:tw-opacity-100">
                        <i className="fa-solid fa-play" />
                      </span>
                    </span>
                    <span className="tw-absolute tw-bottom-1.5 tw-right-1.5 tw-rounded tw-bg-black/70 tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-white">
                      {fmtDuration(asset.duration)}
                    </span>
                  </button>

                  <div className="tw-p-3">
                    <h4 className="tw-truncate tw-text-sm tw-font-semibold tw-text-heading" title={asset.title}>{asset.title}</h4>
                    <div className="tw-mt-1 tw-flex tw-items-center tw-justify-between tw-text-xs tw-text-content-faint">
                      <span>{fmtSize(asset.file_size)}</span>
                      <Badge tone={asset.status === 'active' ? 'good' : 'neutral'}>{asset.status}</Badge>
                    </div>
                    <div className="tw-mt-3 tw-flex tw-gap-1.5">
                      <Button variant="secondary" size="sm" icon="fa-play" className="tw-flex-1" onClick={() => setPreviewAsset(asset)}>Watch</Button>
                      <Button variant="ghost" size="sm" icon="fa-pen" onClick={() => openEditAsset(asset)} title="Edit" />
                      <Button variant="ghost" size="sm" icon="fa-trash" onClick={() => deleteAsset(asset)} title="Delete" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Create/Edit Directory Modal */}
      <Modal show={showDirModal} onHide={() => setShowDirModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>{editingDir ? 'Edit Directory' : 'Create Directory'}</Modal.Title></Modal.Header>
        <form onSubmit={saveDirectory}>
          <Modal.Body className="tw-space-y-4">
            <Field label="Directory name" required>
              <Input value={dirForm.name} onChange={(e) => setDirForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Sunday Sermons" required />
            </Field>
            <Field label="Description">
              <Input value={dirForm.description} onChange={(e) => setDirForm((f) => ({ ...f, description: e.target.value }))} placeholder="What lives in this directory?" />
            </Field>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={() => setShowDirModal(false)}>Cancel</Button>
            <Button type="submit" disabled={savingDir}>{savingDir ? 'Saving…' : editingDir ? 'Save Changes' : 'Create Directory'}</Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Preview Modal */}
      <Modal show={!!previewAsset} onHide={() => setPreviewAsset(null)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>{previewAsset?.title}</Modal.Title></Modal.Header>
        <Modal.Body className="tw-p-0">
          {previewAsset && (
            <video
              key={previewAsset.id}
              src={VodService.getAssetStreamUrl(previewAsset.id)}
              controls autoPlay
              className="tw-w-full tw-bg-black"
              style={{ maxHeight: '70vh' }}
            />
          )}
        </Modal.Body>
        {previewAsset?.description && (
          <Modal.Footer className="tw-block">
            <p className="tw-m-0 tw-text-sm tw-text-content-muted">{previewAsset.description}</p>
          </Modal.Footer>
        )}
      </Modal>

      {/* Edit Video Modal */}
      <Modal show={!!editingAsset} onHide={() => setEditingAsset(null)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Video</Modal.Title></Modal.Header>
        <form onSubmit={async (e) => {
          e.preventDefault();
          setSavingAsset(true);
          try {
            const res = await VodService.updateAsset(editingAsset.id, assetForm);
            if (res.success) {
              toast.success('Video updated');
              setAssets((prev) => prev.map((a) => (a.id === editingAsset.id ? { ...a, ...assetForm } : a)));
              setEditingAsset(null);
            }
          } catch { toast.error('Failed to update video'); } finally { setSavingAsset(false); }
        }}>
          <Modal.Body className="tw-space-y-4">
            <Field label="Title" required>
              <Input value={assetForm.title} onChange={(e) => setAssetForm((f) => ({ ...f, title: e.target.value }))} required />
            </Field>
            <Field label="Description">
              <Input value={assetForm.description} onChange={(e) => setAssetForm((f) => ({ ...f, description: e.target.value }))} />
            </Field>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={() => setEditingAsset(null)}>Cancel</Button>
            <Button type="submit" disabled={savingAsset}>{savingAsset ? 'Saving…' : 'Save Changes'}</Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default MediaDirectoryManager;
